import React, { useCallback, useEffect, useRef, useState } from "react";
import { DisplayMode } from "./tvDevices";
import styles from "./TvPage.module.scss";

/**
 * Forhåndsvisning av det en TV viser nå: WebTV sin egen visning (ibønn.no/tv/preview.html) i en iframe, med samme
 * innhold som TV-en får (bønnetider, plakater, visningsplan). Plakatene bytter på samme sekund som på TV-en.
 */
export const WEBTV_PREVIEW_URL = "https://xn--ibnn-hra.no/tv/preview.html";
const PREVIEW_ORIGIN = new URL(WEBTV_PREVIEW_URL).origin;

/** Innholdet hentes på nytt hvert minutt, som på TV-en. */
const REFRESH_MS = 60_000;

interface Props {
  orgNr: string;
  deviceId: number;
  name: string;
  mode: DisplayMode;
  screen: "display" | "modes";
  fetchPreview: (orgNr: string, deviceId: number) => Promise<unknown>;
}

const TvPreview: React.FC<Props> = ({
  orgNr,
  deviceId,
  name,
  mode,
  screen,
  fetchPreview,
}) => {
  const frame = useRef<HTMLIFrameElement>(null);
  const [snapshot, setSnapshot] = useState<unknown>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    const data = await fetchPreview(orgNr, deviceId);
    if (data && typeof data === "object" && "mosque" in (data as object)) {
      setSnapshot(data);
      setFailed(false);
    } else {
      setFailed(true);
    }
  }, [orgNr, deviceId, fetchPreview]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // Visningen sier fra når den er klar til å ta imot innhold.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (
        e.origin === PREVIEW_ORIGIN &&
        e.source === frame.current?.contentWindow &&
        e.data?.type === "ibonn-tv-preview-ready"
      ) {
        setReady(true);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    if (!ready || !snapshot) return;
    frame.current?.contentWindow?.postMessage(
      { type: "ibonn-tv-preview", snapshot, mode, screen },
      PREVIEW_ORIGIN,
    );
  }, [ready, snapshot, mode, screen]);

  return (
    <div className={styles.screen}>
      <iframe
        ref={frame}
        src={WEBTV_PREVIEW_URL}
        title={`Det «${name}» viser nå`}
        className={styles.screenFrame}
        tabIndex={-1}
        loading="lazy"
        sandbox="allow-scripts allow-same-origin"
      />
      {(!snapshot || failed) && (
        <div className={styles.screenOverlay}>
          {failed ? "Kunne ikke hente forhåndsvisningen." : "Henter …"}
        </div>
      )}
    </div>
  );
};

export default TvPreview;
